import type { PlaygroundTestProgress, PlaygroundTestSuiteResult } from '@gotti/playground/browser';
import { parseErrorFrames } from '@gotti/sandbox-test';
import type { PlaygroundStepView, SuiteResult, TestItemState, TestProgressItem, TestResult, TestRunResult } from './types';

/**
 * One playground test as the run left it — what the Tests panel, the results panel and the `test-results` payload all
 * read, so the three count it once and alike. A playground is a suite; each of its tests is one test.
 */
export interface PlaygroundTestRun {
  /** The playground's name and the file that declares it. */
  playground: string;
  file: string;
  /** The test as its mount normalized it: its id, its label, and its steps in order. */
  test: { id: string; label: string; steps: { label: string; atTime: number }[] };
  /**
   * How the run ended for it: the suite result of the run it was in, a run the person cancelled, the site's bound on
   * the test ending it (`boundMs`: its timeoutMs and 5 s), the site's bound on the playground's `ready` and `game` gates
   * ending the run before any test started, or `runTests` rejecting (a timeline wait past the test's `timeoutMs`).
   */
  outcome:
    | { kind: 'result'; suite: PlaygroundTestSuiteResult }
    | { kind: 'cancelled' }
    | { kind: 'timed-out'; boundMs: number }
    | { kind: 'gates-timed-out'; boundMs: number }
    | { kind: 'rejected'; error: unknown };
  /** The last progress snapshot the run sent for it, if any: its steps' and checks' states. */
  progress?: PlaygroundTestProgress;
  /** The console lines step `stepIndex` produced. */
  logsFor: (stepIndex: number) => readonly string[];
}

const stepState = (state: string): PlaygroundStepView['state'] =>
  state === 'running' || state === 'passed' || state === 'failed' ? state : 'pending';

/** An error a step raised, without the step: `<side> <phase> › <assertion>: <message>`. */
const describeError = (error: { message: string; side: string; phase: string; assertion?: string }): string =>
  `${error.side} ${error.phase}${error.assertion ? ` › ${error.assertion}` : ''}: ${error.message}`;

/** The test's steps: from its last progress snapshot when there is one, else every step pending; each with its errors and console lines. */
const stepsOf = (run: Pick<PlaygroundTestRun, 'test' | 'progress' | 'logsFor'>, ran?: PlaygroundTestSuiteResult['tests'][number]): PlaygroundStepView[] =>
  run.test.steps.map((step, index) => {
    const live = run.progress?.steps[index];
    const errors = (ran?.steps[index]?.errors ?? []).map(describeError);
    return {
      label: step.label,
      atTime: step.atTime,
      state: live ? stepState(live.state) : 'pending',
      ...(live?.durationMs === undefined ? {} : { durationMs: live.durationMs }),
      errors,
      logs: [...run.logsFor(index)],
      checks: (live?.sides ?? []).flatMap((side) => side.phases.flatMap((phase) => phase.assertions.map((assertion) => ({
        side: side.side,
        phase: phase.phase,
        label: assertion.label,
        state: assertion.state,
        ...(assertion.errorMessage === undefined ? {} : { errorMessage: assertion.errorMessage }),
        ...(assertion.durationMs === undefined ? {} : { durationMs: assertion.durationMs }),
      })))),
    };
  });

/** The test's result: `passed`, `failed` with its first error named by step, or `error` when the run never finished it. */
export function playgroundTestResult(run: PlaygroundTestRun): TestResult {
  const base = { name: run.test.label, suiteName: run.playground, file: run.file };
  const tree = (ran?: PlaygroundTestSuiteResult['tests'][number]) => ({ playground: run.playground, id: run.test.id, steps: stepsOf(run, ran) });
  const { outcome } = run;
  if (outcome.kind === 'cancelled') {
    return { ...base, status: 'error', error: { name: 'PlaygroundRunCancelledError', message: 'Playground test run cancelled.' }, playground: tree() };
  }
  if (outcome.kind === 'timed-out') {
    const message = `Test "${run.test.label}" did not finish within ${outcome.boundMs} ms (its timeoutMs and 5 s).`;
    return { ...base, status: 'error', error: { name: 'PlaygroundTimeoutError', message }, playground: tree() };
  }
  if (outcome.kind === 'gates-timed-out') {
    const message = `Playground "${run.playground}" did not get past its ready and game gates within ${outcome.boundMs} ms (their budget and 5 s), so "${run.test.label}" never ran.`;
    return { ...base, status: 'error', error: { name: 'PlaygroundTimeoutError', message }, playground: tree() };
  }
  if (outcome.kind === 'rejected') {
    const err = outcome.error;
    const error = err instanceof Error ? { name: err.name || 'Error', message: err.message } : { name: 'Error', message: String(err) };
    return { ...base, status: 'error', error, playground: tree() };
  }
  const ran = outcome.suite.tests.find((test) => test.id === run.test.id);
  if (!ran) {
    return { ...base, status: 'error', error: { name: 'PlaygroundSuiteError', message: outcome.suite.error ?? 'The playground ran no such test.' }, playground: tree() };
  }
  const duration = { duration: ran.durationMs };
  if (ran.success) return { ...base, status: 'passed', ...duration, playground: tree(ran) };
  const failingStep = ran.steps.find((step) => step.errors.length > 0);
  const first = failingStep?.errors[0] ?? ran.errors[0];
  const message = first
    ? `${failingStep ? `[${failingStep.label}] ` : ''}${describeError(first)}`
    : `Test "${run.test.label}" failed.`;
  const errorFrames = parseErrorFrames(first?.stack);
  return {
    ...base,
    status: 'failed',
    ...duration,
    error: { name: 'PlaygroundTestError', message, ...(errorFrames ? { errorFrames } : {}) },
    playground: tree(ran),
  };
}

/** A playground as one suite of its tests. */
export function playgroundSuiteResult(playground: string, file: string, tests: TestResult[]): SuiteResult {
  return {
    name: playground,
    file,
    tests,
    passed: tests.filter((test) => test.status === 'passed').length,
    failed: tests.filter((test) => test.status === 'failed' || test.status === 'error').length,
    skipped: tests.filter((test) => test.status === 'skipped').length,
  };
}

/** A playground file that failed to load, as a spec that fails to load is reported: a suite with one `load error` test. */
export function playgroundLoadErrorSuite(file: string, message: string): SuiteResult {
  return {
    name: file,
    file,
    passed: 0,
    failed: 1,
    skipped: 0,
    tests: [{ name: 'load error', suiteName: file, file, status: 'error', duration: 0, error: { name: 'LoadError', message } }],
  };
}

const itemState = (state: PlaygroundTestProgress['state']): TestItemState => (state === 'awaiting' ? 'pending' : state);

/** A progress snapshot as the test's live row. */
export function playgroundProgressItem(progress: PlaygroundTestProgress, logsFor: (stepIndex: number) => readonly string[]): TestProgressItem {
  const steps = progress.steps.map((step) => ({ label: step.label, atTime: step.atTime }));
  return {
    name: progress.label,
    state: itemState(progress.state),
    ...(progress.durationMs === undefined ? {} : { duration: progress.durationMs }),
    steps: stepsOf({ test: { id: progress.testId, label: progress.label, steps }, progress, logsFor }),
  };
}

/** The playground mount a `defineSandboxTest` test runs on — the name both test booters mount it under. */
export const DSL_PLAYGROUND = 'sandbox-test';

/** The key a test's DSL steps are kept under: its file, its suite and its name, as the engine reports them. */
export const dslTestKey = (file: string | undefined, suiteName: string, testName: string): string => `${file ?? ''}::${suiteName}::${testName}`;

/**
 * A `defineSandboxTest` test's steps as the Tests panel shows them, from its progress on the playground's runner. No
 * step has console lines of its own: the engine captures a DSL test's logs per test, on its result.
 */
export function dslStepViews(progress: PlaygroundTestProgress): PlaygroundStepView[] {
  const steps = progress.steps.map((step) => ({ label: step.label, atTime: step.atTime }));
  return stepsOf({ test: { id: progress.testId, label: progress.label, steps }, progress, logsFor: () => [] });
}

/**
 * `result` as the Tests panel shows it: each `defineSandboxTest` test given the steps it ran on the playground's runner
 * (`steps`, by `dslTestKey`). The panel's view only — the result a run returns and posts stays the engine's, the wire
 * shape the AI pipeline byte-compares with the node runner's.
 */
export function withDslSteps(
  result: TestRunResult | null,
  steps: ReadonlyMap<string, { id: string; steps: PlaygroundStepView[] }>,
): TestRunResult | null {
  if (!result || steps.size === 0) return result;
  return {
    ...result,
    suites: result.suites.map((suite) => ({
      ...suite,
      tests: suite.tests.map((test) => {
        const ran = test.playground ? undefined : steps.get(dslTestKey(test.file ?? suite.file, suite.name, test.name));
        return ran ? { ...test, playground: { playground: DSL_PLAYGROUND, id: ran.id, steps: ran.steps } } : test;
      }),
    })),
  };
}
